import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import { getEventTimezone, toEventTime, toEventWallClock } from 'utils';

export const SCHEDULE = { ALWAYS: 'always', DAY: 'day', RANGE: 'range' };

export const PAST_NOTE =
	'Past sections stay checkable. They leave current-only widgets and collapse on full checklist pages.';

const DAY = 'ddd, MMM D';
const SHORT_DAY = 'MMM D';
const TIME = 'h:mm A';

// The picker's calendar day, anchored in event time so the browser zone never shifts it.
export const oneDayRange = (event, date) => {
	const day = { year: date.year(), month: date.month(), date: date.date() };
	const timezone = getEventTimezone(event);
	return {
		appears_at: moment.tz(day, timezone).toISOString(),
		current_until: moment.tz({ ...day, hour: 23, minute: 59 }, timezone).toISOString(),
	};
};

export const toIsoOrNull = value => {
	if (!value) return null;
	const parsed = moment(value);
	return parsed.isValid() ? parsed.toISOString() : null;
};

const format = (event, timestamp, pattern) => toEventTime(event, timestamp).format(pattern);

const at = (event, timestamp) =>
	`${format(event, timestamp, TIME)} on ${format(event, timestamp, DAY)}`;

export const scheduleDay = (event, { appears_at, current_until }) => {
	if (!appears_at) return null;
	const day = toEventWallClock(event, appears_at);
	return isEqual(oneDayRange(event, day), { appears_at, current_until }) ? day : null;
};

export const scheduleMode = (event, section) => {
	if (!section.appears_at && !section.current_until) return SCHEDULE.ALWAYS;
	return scheduleDay(event, section) ? SCHEDULE.DAY : SCHEDULE.RANGE;
};

// One day with no start date uses today in event time, so the picker never opens on an empty day.
export const applyMode = (event, section, mode) => {
	if (mode === SCHEDULE.ALWAYS) return { appears_at: null, current_until: null };
	if (mode === SCHEDULE.DAY) return oneDayRange(event, toEventWallClock(event, section.appears_at));
	return {};
};

export const scheduleLabel = (event, section) => {
	const { appears_at, current_until } = section;
	const mode = scheduleMode(event, section);
	if (mode === SCHEDULE.ALWAYS) return 'Always shown';
	if (mode === SCHEDULE.DAY) return `${format(event, appears_at, DAY)} only`;
	if (!appears_at) return `Until ${format(event, current_until, DAY)}`;
	if (!current_until) return `From ${format(event, appears_at, DAY)}`;
	return `${format(event, appears_at, SHORT_DAY)} – ${format(event, current_until, SHORT_DAY)}`;
};

export const scheduleSentence = (event, { appears_at, current_until }) => {
	if (!appears_at && !current_until)
		return 'Shown as soon as you save, and never moves to past sections.';
	const shows = appears_at
		? `Shows up in the app at ${at(event, appears_at)}`
		: 'Shows up as soon as you save';
	const moves = current_until
		? `moves to past sections at ${at(event, current_until)}`
		: 'never moves to past sections';
	return `${shows} and ${moves}.`;
};
